import { applicationFormSteps } from '@authorizedInstaller/constants';
import { AuthorizedInstallersContext } from '@authorizedInstaller/contexts';
import { AuthorizedInstallerApplicationSchema } from '@authorizedInstaller/schemas';
import { useSaveApplicationMutation } from '@authorizedInstaller/services';
import { AuthorizedInstallerApplication } from '@authorizedInstaller/types/authorizedInstallers';
import { PublicLayoutContent } from '@components/layout/public';
import { useAppDispatch } from '@hooks/generic';
import { useAuth } from '@hooks/useAuth';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import {
    Button,
    CircularProgress,
    MobileStepper,
    Step,
    StepButton,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';
import { usePatchSelfMutation } from '@services/accounts';
import { setTitle } from '@store/appSlice';
import { handleError } from '@utils/common';
import clsx from 'clsx';
import { FormikTouched, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ContactStep } from './Steps/ContactStep';
import { GeneralStep } from './Steps/GeneralStep';
import { SuccessStep } from './Steps/Success';
import { TermsStep } from './Steps/TermsStep';
import { UploadStep } from './Steps/UploadStep';

const AuthorizedInstallerApplicationForm = () => {
    const dispatch = useAppDispatch();
    const [activeStep, setActiveStep] = useState(0);
    const [updateSelf, { isLoading: isUpdateSelfLoading }] = usePatchSelfMutation();
    const [saveApplication, { isLoading: isSaveApplicationLoading }] = useSaveApplicationMutation();
    const { user, isUserLoading } = useAuth();

    const disableForm = useMemo(
        () => isUpdateSelfLoading || isSaveApplicationLoading || isUserLoading,
        [isSaveApplicationLoading, isUpdateSelfLoading, isUserLoading]
    );

    useEffect(() => {
        dispatch(setTitle('Authorized Installers: Application'));
    }, [dispatch]);

    const handleBack = useCallback(() => {
        setActiveStep((prevStep) => prevStep - 1);
    }, []);

    const [applicationSuccess, setApplicationSuccess] = useState(false);

    const formik = useFormik<Partial<AuthorizedInstallerApplication>>({
        initialValues: {
            first_name: user?.first_name ?? '',
            last_name: user?.last_name ?? '',
            company_name: user?.company_name ?? '',
            distributor: '',
            sales_manager: '',
            mobile_phone: user?.mobile_phone ?? '',
            email: user?.email,
            address1: user?.address1,
            city: user?.city ?? '',
            country_code: user?.country_id ?? '',
            state: user?.state ?? '',
            certificate_files: [],
            installation_files: [],
            accept_terms: false,
        },
        validationSchema: AuthorizedInstallerApplicationSchema,
        validateOnMount: true,
        onSubmit: async (values) => {
            try {
                await updateSelf({
                    first_name: values.first_name ?? '',
                    last_name: values.last_name ?? '',
                    mobile_phone: values.mobile_phone ?? '',
                    country_code: values.country_code ?? '',
                    address1: values.address1 ?? '',
                    address2: '',
                    city: values.city ?? '',
                    company_name: values.company_name ?? '',
                }).unwrap();

                const authorizedInstallerData = new FormData();
                authorizedInstallerData.append('distributor', values.distributor ?? '');
                authorizedInstallerData.append('sales_manager', values.sales_manager ?? '');
                for (const installation of values.installation_files ?? []) {
                    authorizedInstallerData.append('installation_files', installation.file);
                }

                for (const certificate of values.certificate_files ?? []) {
                    authorizedInstallerData.append('certificate_files', certificate.file);
                }

                await saveApplication(authorizedInstallerData).unwrap();

                setApplicationSuccess(true);
            } catch (err) {
                handleError('An error occured while saving your information', err);
            }
        },
        enableReinitialize: true,
    });

    const stepErrors = useMemo(
        () =>
            [0, 1, 2].reduce((acc, step) => {
                acc[step] = applicationFormSteps[step].fields.some(
                    (field) => formik.touched[field] && formik.errors[field]
                );
                return acc;
            }, {} as Record<number, boolean>),
        [formik.touched, formik.errors]
    );

    const hasPreviousErrors = useCallback(
        (step: number) =>
            Object.entries(stepErrors)
                .filter(([key]) => +key < step)
                .some(([, value]) => !!value),
        [stepErrors]
    );

    const validateStep = useCallback(async () => {
        const touchedFields = applicationFormSteps[activeStep].fields.reduce((acc, field) => {
            acc[field] = true;
            return acc;
        }, {} as FormikTouched<Partial<AuthorizedInstallerApplication>>);
        formik.setTouched(touchedFields);

        const errors = await formik.validateForm();
        const currentStepErrors = applicationFormSteps[activeStep].fields.some((field) => errors[field]);

        if (currentStepErrors) {
            throw false;
        }
    }, [activeStep, formik]);

    const handleStepChange = useCallback(
        async (step: number) => {
            try {
                if (applicationSuccess) return;

                await validateStep();
                setActiveStep(step);
            } catch (err) {
                const el = document.querySelector('.Mui-error, [data-error]');
                const element = el?.parentElement ?? el;
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                if (step < activeStep) {
                    setActiveStep(step);
                }
            }
        },
        [activeStep, validateStep, applicationSuccess]
    );

    const step = useMemo(() => {
        switch (activeStep) {
            default:
            case 0:
                return <GeneralStep />;
            case 1:
                return <ContactStep />;
            case 2:
                return <UploadStep fileFieldName="certificate_files" />;
            case 3:
                return <UploadStep fileFieldName="installation_files" />;
            case 4:
                return <TermsStep />;
        }
    }, [activeStep]);

    return (
        <PublicLayoutContent>
            <AuthorizedInstallersContext.Provider value={{ formik, user }}>
                <div className="flex flex-col pt-12 px-4 pb-32 md:pt-11 md:px-11 md:pb-32 gap-11 h-full">
                    <Typography variant="h4" fontWeight={700}>
                        Application to the Victron Authorized Installer Program
                    </Typography>
                    <div className="flex flex-col bg-white py-6 max-h-[728px] h-fit px-4 md:px-8 md:pt-8 md:pb-10 md:mb-0 md:h-[728px]">
                        {!applicationSuccess ? (
                            <>
                                <div className="flex flex-col gap-5 grow">
                                    <div className="flex flex-col gap-11">
                                        <div className="flex flex-col gap-2">
                                            <Typography variant="h4" fontWeight={700}>
                                                {applicationFormSteps[activeStep].title}
                                            </Typography>
                                            <Typography>{applicationFormSteps[activeStep].description}</Typography>
                                        </div>
                                        {step}
                                    </div>
                                </div>
                                <Stepper activeStep={activeStep} className="hidden md:flex mt-12">
                                    {applicationFormSteps.map((step, i) => (
                                        <Step key={step.title} disabled={disableForm || hasPreviousErrors(i)}>
                                            <StepButton
                                                className="!py-2"
                                                onClick={() => handleStepChange(i)}
                                                disabled={hasPreviousErrors(i)}
                                                icon={
                                                    i === 0 && formik.isValidating ? (
                                                        <CircularProgress size={20} />
                                                    ) : undefined
                                                }
                                            >
                                                <StepLabel
                                                    slotProps={{
                                                        label: {
                                                            className: 'font-medium',
                                                        },
                                                    }}
                                                    error={stepErrors[i] && activeStep !== i}
                                                >
                                                    {step.title}
                                                </StepLabel>
                                            </StepButton>
                                        </Step>
                                    ))}
                                </Stepper>
                                <div className="hidden mt-4 justify-between md:flex">
                                    <Button
                                        className="rounded-sm min-w-24"
                                        color="inherit"
                                        disabled={activeStep === 0}
                                        onClick={handleBack}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        disabled={disableForm}
                                        className="rounded-sm min-w-24"
                                        onClick={
                                            activeStep !== applicationFormSteps.length - 1
                                                ? () => handleStepChange(activeStep + 1)
                                                : () => formik.handleSubmit()
                                        }
                                        startIcon={
                                            formik.isSubmitting || disableForm ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : null
                                        }
                                    >
                                        <span>
                                            {activeStep === applicationFormSteps.length - 1 ? 'Submit' : 'Next'}
                                        </span>
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <SuccessStep />
                        )}
                    </div>
                </div>
                {!applicationSuccess && (
                    <div className="flex w-full justify-between md:hidden">
                        <div
                            className={clsx({
                                'h-2': activeStep === 0,
                                'h-20': activeStep === 1,
                            })}
                        ></div>
                        <MobileStepper
                            activeStep={activeStep}
                            className="fixed h-14 justify-between w-full"
                            steps={applicationFormSteps.length + 1}
                            backButton={
                                <Button
                                    className="rounded-sm min-w-24 border-0"
                                    color="inherit"
                                    startIcon={<ChevronLeft />}
                                    variant="outlined"
                                    disabled={activeStep === 0}
                                    onClick={handleBack}
                                >
                                    Back
                                </Button>
                            }
                            nextButton={
                                <Button
                                    disabled={disableForm}
                                    className="rounded-sm min-w-24 border-0"
                                    endIcon={<ChevronRight />}
                                    variant="outlined"
                                    onClick={
                                        activeStep !== applicationFormSteps.length - 1
                                            ? () => handleStepChange(activeStep + 1)
                                            : () => formik.handleSubmit()
                                    }
                                >
                                    {activeStep === applicationFormSteps.length - 1 ? 'Submit' : 'Next'}
                                </Button>
                            }
                        />
                    </div>
                )}
            </AuthorizedInstallersContext.Provider>
        </PublicLayoutContent>
    );
};

export { AuthorizedInstallerApplicationForm };
